import React, { useEffect, FunctionComponent, useMemo, useState } from 'react';
import {
  Redirect,
  Route,
  BrowserRouter as Router,
  Switch,
} from 'react-router-dom';
import { connect, ConnectedProps } from 'react-redux';
import { RootState } from '../../app/store';
import '@progress/kendo-theme-default/dist/all.css';
import NotFound from '../../components/NotFound/NotFound.component';
import { Wrap } from '../../assets/styledAssets/grid';
import { accentColorPrim } from '../../assets/styledAssets/vars';
import { fetchUser } from './App.actions';
import SideBar from '../../components/SideBar/SideBar.component';
import ProtectedRoute from '../../components/ProtectedRoute/ProtectedRoute.component';
import TestResults from '../../features/TestResults/TestResults.container';
import Users from '../../features/Users/Users.container';
import Groups from '../../features/Groups/Groups.container';
import Questionnaires from '../../features/Questionnaires/Questionnaires.container';
import Spaces from '../../features/Spaces/Spaces.container';
import Profile from '../../features/Profile/Profile.container';
import Spreadsheet from '../../features/Spreadsheet/Spreadsheet.container';
import SelectAccount from '../../components/SelectAccount/SelectAccount.component';
import { setSelectedAccount } from './App.slice';
import Settings from '../../features/Settings/Settings.container';
import Qualification from '../../features/Qualification/Qualification.container';
import { Roles } from '../../domains/app';
import HomePage from '../../features/HomePage/HomePage.container';
import Achievements from '../../features/Achievements/Achievements.container';
import SiteOwnerTestResults from '../../features/SiteOwnerTestResults/SiteOwnerTestResults.container';
import TestTypes from '../../features/TestTypes/TestTypes.container';
import WorldRecords from '../../features/WorldRecords/WorldRecords.container';
import {Header, StyledBody} from './styles';
import Button from '../../components/Button/Button.component';
import { BarIcon, LogoSm } from '../../components/Icons/Icons.component';
import { fetchListLanguges } from '../../service/Questionnarie.service';
import { setListLanguages } from '../LanguageProvider/LanguageProvider.slice';

const connector = connect(
  (state: RootState) => ({
    user: state.app.user,
    selectedAccount: state.app.selectedAccount,
  }),
  {
    fetchUser,
    setSelectedAccount,
    setListLanguages,
  },
);

type PropsFromRedux = ConnectedProps<typeof connector>;

const AppContainer: FunctionComponent<PropsFromRedux> = ({
  user,
  fetchUser,
  selectedAccount,
  setListLanguages,
}) => {
  const [isSidebarShow, setSidebarShow] = useState(false);
  useEffect(() => {
    fetchUser();
    fetchListLanguges().then((res) => {
      setListLanguages(res);
    });
    // eslint-disable-next-line
  }, []);

  const redirectPath = useMemo((): string => {
    if (!selectedAccount) {
      return '/select-account';
    }
    if (user) {
      const role: number = selectedAccount.role as number;
      if (role & Roles.User) {
        return '/home';
      }
      if (role & Roles.Administrator || role & Roles.SiteOwner) {
        return '/users';
      }
      if (role & Roles.SpaceOwner || role & Roles.SpaceAdministrator) {
        return '/members';
      }
      return '/test-results';
    }

    return '/';
  }, [selectedAccount, user]);

  function handleCloseBar() {
    if (window.innerWidth < 1280) {
      //TODO check for userAgent
      setSidebarShow(!isSidebarShow);
    }
  }
  console.log(isSidebarShow)
  return (
    <Router>
      {user && selectedAccount && (
        <Header>
          <Wrap grow={'1'}>
            <Button
              onClick={() => setSidebarShow(!isSidebarShow)}
              icon
              transparent
            >
              <BarIcon color={accentColorPrim} />
            </Button>
          </Wrap>
          <Wrap grow={'1'}>
            <LogoSm height={'24'} color={accentColorPrim} />
          </Wrap>
        </Header>
      )}
      <Wrap grow={'1'} style={{zIndex: 5}}>
        {user && selectedAccount && (
          <SideBar
            isSidebarShow={isSidebarShow}
            onBarClose={() => handleCloseBar()}
            className={isSidebarShow ? 'active' : ''}
            user={user}
            selectedAccount={selectedAccount}
          />
        )}
        <StyledBody isSidebarShow={isSidebarShow}>
          <Switch>
            <Route
              exact
              path="/"
              render={() => <Redirect to={redirectPath} />}
            />
            <Route path={'/select-account'} component={SelectAccount} />
            <ProtectedRoute path={'/users'} component={Users} />
            <ProtectedRoute path={'/members'} component={Users} />
            <ProtectedRoute path={'/groups'} component={Groups} />
            <ProtectedRoute
              path={'/questionnaires'}
              render={() => <Questionnaires />}
            />
            <ProtectedRoute path={'/spaces'} component={Spaces} />
            <ProtectedRoute path={'/profile'} component={Profile} />
            <ProtectedRoute path={'/test-results'} component={TestResults} />
            <ProtectedRoute
              path={'/owner-test-results'}
              component={SiteOwnerTestResults}
            />
            <ProtectedRoute path={'/home'} component={HomePage} />
            <ProtectedRoute
              path={'/qualifications'}
              component={Qualification}
            />
            <ProtectedRoute path={'/achievements'} component={Achievements} />
            <ProtectedRoute path={'/settings'} component={Settings} />
            <ProtectedRoute path={'/test-types'} component={TestTypes} />
            <ProtectedRoute path={'/world-records'} component={WorldRecords} />
            <Route path={'/spreadsheet'} component={Spreadsheet} />
            <Route path={''} component={NotFound} />
          </Switch>
        </StyledBody>
      </Wrap>
    </Router>
  );
};

export default connector(AppContainer);
